/**
 * AuthCallback — lands here after the backend verifies a magic link or OAuth
 * exchange and redirects to `{PUBLIC_APP_DOMAIN}/auth/callback#token=<jwt>`.
 *
 * Flow:
 *   1. Read the session JWT from the URL fragment (`#token=...`). A fragment is
 *      used (not a query string) so the token never reaches the server, the
 *      Referer header, or shared history.
 *   2. Persist the token, fetch the profile (`GET /me`), hydrate the auth store.
 *   3. Replace the URL (strip the token) and redirect to the app.
 *
 * On any error (missing/invalid token, /me failure) we bounce to /login with a
 * message.
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/features/auth/authApi';
import { useAuthStore } from '@/features/auth/authStore';

const TOKEN_KEY = 'rt.auth.token';

function readTokenFromHash(): string | null {
  // Fragment looks like "#token=eyJ...". Also tolerate "?token=" as a fallback.
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const fromHash = new URLSearchParams(hash).get('token');
  if (fromHash) return fromHash;
  return new URLSearchParams(window.location.search).get('token');
}

export function AuthCallback(): ReactElement {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const setUser = useAuthStore((s) => s.setUser);
  const [error, setError] = useState<string | null>(null);
  // React 18 StrictMode double-invokes effects in dev; guard so we run once.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    async function complete(): Promise<void> {
      const token = readTokenFromHash();
      if (!token) {
        setError('Link inválido ou expirado.');
        setTimeout(() => navigate('/login?error=magic_link_invalid', { replace: true }), 1200);
        return;
      }

      // Persist the token first so the authenticated /me call carries it.
      try {
        localStorage.setItem(TOKEN_KEY, token);
      } catch {
        /* private mode — the store still holds it in memory below */
      }

      try {
        const user = await authApi.me();
        // Hydrate the store (login persists the token again; harmless).
        login({ token, user });
        setUser(user);
        // Strip the token from the URL before navigating away.
        window.history.replaceState(null, '', '/auth/callback');
        navigate('/', { replace: true });
      } catch {
        try {
          localStorage.removeItem(TOKEN_KEY);
        } catch {
          /* ignore */
        }
        setError('Não foi possível concluir o login.');
        setTimeout(() => navigate('/login?error=session_failed', { replace: true }), 1200);
      }
    }

    void complete();
  }, [login, setUser, navigate]);

  return (
    <main style={styles.page}>
      <section style={styles.card}>
        <h1 style={styles.title}>RealtimeTranslator</h1>
        <p style={styles.text}>{error ?? 'Concluindo login…'}</p>
      </section>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    background: 'var(--rt-bg)',
    color: 'var(--rt-text)',
    padding: '1.5rem',
  },
  card: {
    width: '100%',
    maxWidth: 380,
    background: 'var(--rt-surface)',
    border: '1px solid var(--rt-border)',
    borderRadius: 12,
    padding: '2rem',
    textAlign: 'center',
  },
  title: { margin: '0 0 0.5rem', fontSize: '1.25rem' },
  text: { margin: 0, color: 'var(--rt-text-muted)', fontSize: '0.9rem' },
};
